import React from "react";
import { View, ScrollView, DimensionValue, Pressable, TouchableOpacity } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { EmptyState } from "./EmptyState";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";
import { FlashList } from "@shopify/flash-list";
import { useI18n } from "@/context/I18nContext";
import { TranslationKeys } from "@chinese-vendor/i18n";
import { cn } from "@/lib/utils";

export interface TableColumn<T> {
  key: string;
  header: string | React.ReactNode;
  width?: DimensionValue;
  renderCell: (item: T) => React.ReactNode;
  flex?: number;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  keyExtractor: (item: T, index: number) => string;
  ListEmptyComponent?: React.ReactNode;
  minWidth?: number;
  limit?: number;
  totalCount?: number;
  page?: number;
  onPageChange?: (page: number) => void;
  onRowPress?: (item: T) => void;
  emptyTitle?: TranslationKeys;
  emptyDescription?: TranslationKeys;
  isLoading?: boolean;
}

export function Table<T>({
  columns,
  data,
  keyExtractor,
  ListEmptyComponent,
  minWidth = 1000,
  limit = 10,
  totalCount = 0,
  page = 1,
  onPageChange,
  onRowPress,
  emptyTitle = "common.table.empty.title",
  emptyDescription = "common.table.empty.description",
  isLoading = false,
}: TableProps<T>) {
  const { t, isRTL } = useI18n();

  const handlePageChange = (newPage: number) => {
    if (onPageChange && newPage >= 1 && newPage <= Math.ceil(totalCount / limit)) {
      onPageChange(newPage);
    }
  };

  const totalPages = Math.ceil(totalCount / limit);
  const showPagination = totalCount > limit;

  const renderPaginationNumbers = () => {
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      let startPage = Math.max(2, page - 1);
      let endPage = Math.min(totalPages - 1, page + 1);

      if (page <= 2) {
        endPage = 3;
      } else if (page >= totalPages - 1) {
        startPage = totalPages - 2;
      }

      if (startPage > 2) {
        pages.push("...");
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalPages - 1) {
        pages.push("...");
      }

      pages.push(totalPages);
    }

    return pages.map((p, index) => {
      if (p === "...") {
        return (
          <View
            key={`ellipsis-${index}`}
            className="w-8 h-8 items-center justify-center border-l border-stroke-50"
          >
            <AppText className="text-dark-text-20 text-base">...</AppText>
          </View>
        );
      }

      const isSelected = p === page;
      return (
        <Pressable
          key={`page-${p}`}
          onPress={() => handlePageChange(p as number)}
          className={`w-8 h-8 items-center justify-center border-l border-stroke-50 ${isSelected ? "bg-primary" : ""}`}
        >
          <AppText
            className={`text-base font-medium ${isSelected ? "text-white" : "text-dark-text-20"}`}
          >
            {p}
          </AppText>
        </Pressable>
      );
    });
  };

  const showingFrom = Math.min((page - 1) * limit + 1, totalCount);
  const showingTo = Math.min(page * limit, totalCount);

  const TableContainer = ({ children }: { children: React.ReactNode }) => {
    return (
      <ScrollView
        showsHorizontalScrollIndicator={false}
        horizontal
        contentContainerStyle={{ flexGrow: 1 }}
      >
        {children}
      </ScrollView>
    );
  };

  const TableHeader = () => (
    <View className="flex-row items-center bg-stroke-20 border-b w-full border-stroke-50 h-[48px]">
      {columns.map((col, idx) => (
        <View
          key={col.key || idx.toString()}
          className={cn("px-6 items-start")}
          style={{ width: col.width, flex: col.flex }}
        >
          {typeof col.header === "string" ? (
            <AppText weight="medium" className="text-dark-text-50 text-base">
              {col.header}
            </AppText>
          ) : (
            col.header
          )}
        </View>
      ))}
    </View>
  );

  const EmptyTableState = () => (
    <>
      <TableContainer>
        <TableHeader />
      </TableContainer>
      <View className="p-8 justify-center items-center min-h-[300px]">
        {ListEmptyComponent || <EmptyState title={emptyTitle} description={emptyDescription} />}
      </View>
    </>
  );

  return (
    <View className="bg-white border border-stroke-50 rounded-lg overflow-hidden w-full">
      {totalCount === 0 ? (
        <EmptyTableState />
      ) : (
        <>
          <TableContainer>
            <View style={{ minWidth }} className="flex-col w-full">
              {/* Header */}
              <TableHeader />

              {/* Body */}
              {isLoading ? (
                <View className="py-16 items-center">
                  <AppText className="text-sm text-dark-text-15">{t("common.loading")}</AppText>
                </View>
              ) : (
                <View style={{ height: data.length * 72, width: "100%" }}>
                  <FlashList
                    data={data}
                    keyExtractor={keyExtractor}
                    scrollEnabled={false}
                    extraData={[columns, data]}
                    renderItem={({ item: row, index }) => {
                      const RowContainer = onRowPress ? TouchableOpacity : View;
                      return (
                        <RowContainer
                          onPress={onRowPress ? () => onRowPress(row) : undefined}
                          activeOpacity={onRowPress ? 0.7 : 1}
                          className={`flex-row items-center min-h-[72px] ${
                            index !== data.length - 1 ? "border-b border-stroke-50" : ""
                          } ${onRowPress ? "hover:bg-stroke-5 active:bg-stroke-10" : ""}`}
                        >
                          {columns.map((col, idx) => (
                            <View
                              key={`${keyExtractor(row, index)}-${col.key || idx.toString()}`}
                              className="px-6 items-start justify-center"
                              style={{ width: col.width, flex: col.flex }}
                            >
                              {col.renderCell(row)}
                            </View>
                          ))}
                        </RowContainer>
                      );
                    }}
                  />
                </View>
              )}
            </View>
          </TableContainer>

          {showPagination && (
            <View className="flex-row items-center justify-between px-6 py-4 border-t border-stroke-50 bg-white">
              <AppText className="text-dark-text-50 text-base font-medium">
                {t("common.table.showing", {
                  from: showingFrom.toString(),
                  to: showingTo.toString(),
                  total: totalCount.toString(),
                })}
              </AppText>

              <View className="flex-row items-center border border-stroke-50 rounded bg-white">
                <Pressable
                  onPress={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="w-8 h-8 items-center justify-center"
                  style={{ opacity: page === 1 ? 0.5 : 1 }}
                >
                  <Icon
                    as={Feather}
                    name={isRTL ? "chevron-right" : "chevron-left"}
                    size={20}
                    color="#6C717B"
                  />
                </Pressable>

                {renderPaginationNumbers()}

                <Pressable
                  onPress={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  className="w-8 h-8 items-center justify-center border-l border-stroke-50"
                  style={{ opacity: page === totalPages ? 0.5 : 1 }}
                >
                  <Icon
                    as={Feather}
                    name={isRTL ? "chevron-left" : "chevron-right"}
                    size={20}
                    color="#6C717B"
                  />
                </Pressable>
              </View>
            </View>
          )}
        </>
      )}
    </View>
  );
}
